import React, { useRef, useState } from "react";
import { Plus, Trash2, Upload, GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { api, resolveImage } from "../../lib/api";
import { toast } from "sonner";
import TipTapEditor from "./TipTapEditor";

const inputCls = "w-full bg-white border border-border rounded-md px-3.5 py-2.5 text-navy text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow";
const labelCls = "block uppercase tracking-[0.14em] text-[11px] font-bold text-navy/60 mb-1.5";

export const ImageField = ({ label, value, category = "general", onChange }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      {label && <span className={labelCls}>{label}</span>}
      <div className="flex items-start gap-3">
        <div className="w-24 h-24 rounded-md overflow-hidden bg-offwhite border border-border shrink-0">
          {value ? <img src={resolveImage(value)} alt="preview" className="w-full h-full object-cover" /> : null}
        </div>
        <div className="flex-1 space-y-2">
          <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" />
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-bold text-dusty border border-dusty/40 rounded-md px-3 py-2 hover:bg-dusty hover:text-white transition-colors disabled:opacity-50"
          >
            <Upload size={14} /> {busy ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const Field = ({ field, value, onChange }) => {
  const { label, type, category } = field;
  if (type === "rich") {
    return (
      <div>
        <span className={labelCls}>{label}</span>
        <TipTapEditor value={value} onChange={onChange} />
      </div>
    );
  }
  if (type === "image") {
    return <ImageField label={label} value={value} category={category} onChange={onChange} />;
  }
  if (type === "textarea") {
    return (
      <div>
        <span className={labelCls}>{label}</span>
        <textarea className={inputCls} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  return (
    <div>
      <span className={labelCls}>{label}</span>
      <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
};

export const ListField = ({ label, items = [], schema, onChange, addLabel = "Add item", newItem = {}, reorderable = false }) => {
  const update = (idx, key, val) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [key]: val } : it));
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { ...newItem }]);
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div>
      <span className={labelCls}>{label}</span>
      <div className="space-y-4">
        {items.map((item, idx) => (
          <div key={idx} className="relative bg-offwhite/60 border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 text-xs text-navy/50"><GripVertical size={14} /> Item {idx + 1}</span>
              <div className="flex items-center gap-1">
                {reorderable && (
                  <>
                    <button
                      type="button"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      className="text-navy/60 hover:text-navy disabled:opacity-30 disabled:cursor-not-allowed p-1"
                      aria-label="Move up"
                      data-testid={`list-item-${idx}-up`}
                    >
                      <ChevronUp size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(idx, 1)}
                      disabled={idx === items.length - 1}
                      className="text-navy/60 hover:text-navy disabled:opacity-30 disabled:cursor-not-allowed p-1"
                      aria-label="Move down"
                      data-testid={`list-item-${idx}-down`}
                    >
                      <ChevronDown size={16} />
                    </button>
                  </>
                )}
                <button type="button" onClick={() => remove(idx)} className="text-destructive hover:opacity-70 p-1" aria-label="Remove">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            {schema.map((f) => (
              <Field key={f.key} field={f} value={item[f.key]} onChange={(v) => update(idx, f.key, v)} />
            ))}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-bold text-navy border border-navy/30 rounded-md px-4 py-2.5 hover:bg-navy hover:text-white transition-colors"
      >
        <Plus size={14} /> {addLabel}
      </button>
    </div>
  );
};

export { inputCls, labelCls };
